import { FacetsManager } from "./FacetsManager";
import {
  DISPLAY_COLOR_NO_FILL,
  FacetTypes,
  PALETTE_ATTRIBUTE_STRING,
  PALETTE_ATTRIBUTE_STRINGLIST,
  UNCLASSIFIED } from
"./Facets";
import { AttributeType } from "../schema/Attribute";
import { sortedArrayHas } from "./sortedArray";

const MultipleMepSystemsFacetColorId = FacetTypes.mepSystems + ':multiple';
const MultipleRoomsFacetColorId = FacetTypes.spaces + ':multiple';

export class PaletteGenerator {

  constructor() {
    this.color = new THREE.Color();
    this.hue = 0; // [0, 360]
    this.hueI = 0; // [0, 10]
    this.saturation = 100; // [30, 100]
    this.lightness = 75; // [25, 75]
  }

  next() {
    // Palette is "consumed": generate differentiable colors
    this.color.setHSL(this.hue / 360, this.saturation / 100, this.lightness / 100);
    let colorHex = '#' + this.color.getHexString();

    // tweak hue, saturation and lightness to generate colors:
    // - increment by the "golden angle" (~= 137 deg): next colors are distinguishable and it's not a divisor of 360
    // - decrement saturation every 10 colors
    // - decrement lightness every 70 colors
    this.hue = (this.hue + 137) % 360;
    this.hueI += 1;
    if (this.hueI === 10) {
      this.hueI = 0;
      this.saturation -= 10;
      // Saturation below 30% is too grey
      if (this.saturation === 30) {
        this.saturation = 100;
        this.lightness -= 10;
        // Lightness below 30% is too dark
        if (this.lightness <= 30) {
          // use pastel colors
          this.lightness = 75;
        }
      }
    }

    return colorHex;
  }
}

function djb2HashStr(str) {
  str += 'ice cream'; // add a little sugar
  let hash = 5381; // djb2 magic number
  for (let i = 0; i < str.length; i++) {
    hash = hash * 33 ^ str.charCodeAt(i);
  }
  return Math.abs(hash); // convert to unsigned int
}

function generateColor(str) {
  const hash = djb2HashStr(str);

  const hue = hash % 361; // [0, 360]
  const saturation = hash % 71 + 30; // [30, 100]
  const lightness = hash % 51 + 25; // [25, 75]

  const color = new THREE.Color();
  color.setHSL(hue / 360, saturation / 100, lightness / 100); // values range [0, 1]
  return '#' + color.getHexString();
}

export function getRestrictedValueColor(dataType, allowedList = [], displayOptions, value) {
  const valueIndex = allowedList.indexOf(value);

  if (valueIndex === -1) {
    /* Extensible list item, generate color */
    return generateColor(value);
  }

  // Special case for value belonging to the list of allowed values
  // there may be displayOptions coming from the attribute definition
  const color = displayOptions?.[value]?.c;

  if (color == null) {
    // Default to a color from the palette
    // Note: contrary to non-restrictive values, the palette is not "consumed" here since we cycle through it
    const palette = dataType === AttributeType.StringList ? PALETTE_ATTRIBUTE_STRINGLIST : PALETTE_ATTRIBUTE_STRING;
    return palette[valueIndex % palette.length];
  }

  if (color !== DISPLAY_COLOR_NO_FILL) {
    return color;
  }

  return null;
}

FacetsManager.prototype._convertColorMap = function (colorMap) {
  const colorMapV4 = {};
  for (let c in colorMap) {
    let hex = colorMap[c];
    if (hex) {
      // THREE's color parsing (which is used under the hood of applyTheme) only copes
      // with RGB hex colors, hence we strip the alpha channel here.
      // TODO: tweak THREE.Color.setStyle, or make it faster some other way
      if (hex.length === 9) {
        hex = hex.slice(0, 7);
      }
      const clr = new THREE.Color(hex);
      colorMapV4[c] = new THREE.Vector4(clr.r, clr.g, clr.b, 0.9);
    }
  }
  return colorMapV4;
};


FacetsManager.prototype.generateColorMap = function (defaultColorMaps) {

  const facetDefs = this.facetDefs;

  //TODO: Disabled the fix for https://jira.autodesk.com/browse/DTWIN-2601 here because it
  //has the side effect of very significantly increaing load time, even for models and views that
  //do not have a color map set at all. We need a solution that perhaps delays color map generation
  //until it is first needed (see Facets.js in dt-client)
  const mergedFacets = this.mergedFacets; //this.collectUnfilteredMergedFacets();

  const colorMaps = defaultColorMaps || {};

  for (let i = 0; i < mergedFacets.length; i++) {
    const facetDef = facetDefs[i];
    const { palette, paletteById } = facetDef;

    let topLevelPaletteClasses;
    if (paletteById) {
      topLevelPaletteClasses = Object.keys(paletteById);
      topLevelPaletteClasses.sort((a, b) => {
        if (a.length > b.length) {
          return -1;
        } else if (a.length < b.length) {
          return 1;
        }
        return a.localeCompare(b);
      });
    }


    let colorGen = new PaletteGenerator();

    if (!colorMaps[facetDef.id]) {
      colorMaps[facetDef.id] = {};
    }

    const colorMap = colorMaps[facetDef.id];
    // The use of RGBA (# + 8 char) indicates user defined value
    const hasUserDefinition = Object.values(colorMap).some((v) => v.length === 9);

    const usedColors = {};
    const reuseColorsByPrefix = facetDef.id === FacetTypes.spaces;

    let j = 0;
    const q = [...mergedFacets[i]];

    while (q.length > 0) {
      const mergedFacetNode = q.shift();

      // skip virtual nodes
      // don't override values as they can be updated by the user
      if (!hasUserDefinition && colorMap[mergedFacetNode.id] === undefined) {
        let colorHex;

        let colorId;
        //Special case of rooms whose names are composed of Name + Number.
        //We achieve something close to color by function by ignoring the room number piece
        if (reuseColorsByPrefix) {
          colorId = mergedFacetNode.label;
          const idx = colorId.lastIndexOf(" ");
          if (idx > 0) {
            colorId = colorId.slice(0, idx);
          }
          colorHex = usedColors[colorId];
        }

        let colorFromParent = false;
        if (mergedFacetNode.parentColorHex) {
          colorHex = mergedFacetNode.parentColorHex;
          colorFromParent = true;
        }

        // respect hierarchical colors
        if ((!colorHex || colorFromParent) && paletteById) {
          if (facetDef.id === FacetTypes.mepSystems) {
            if (mergedFacetNode.glowEffectId === -1) {
              colorHex = paletteById[UNCLASSIFIED];
            } else {
              colorHex = paletteById[mergedFacetNode.glowEffectId];
            }
          } else {
            for (let i = 0; i < topLevelPaletteClasses.length; i++) {
              if (mergedFacetNode.id.startsWith(topLevelPaletteClasses[i])) {
                colorHex = paletteById[topLevelPaletteClasses[i]];
                break;
              }
            }
          }
        } else if (!colorHex && facetDef.attribute?.allowedValues?.list) {
          const dataType = facetDef.attribute.dataType;
          const allowedList = facetDef.attribute.allowedValues.list;
          const displayOptions = facetDef.attribute.displayOptions;
          const value = mergedFacetNode.id;

          colorHex = getRestrictedValueColor(dataType, allowedList, displayOptions, value);

          if (!colorHex) continue; // no fill
        } else if (!colorHex) {
          colorHex = palette[j];
        }

        if (!colorHex) {
          // Palette is "consumed": generate differentiable colors
          colorHex = colorGen.next();

          if (colorId) {
            usedColors[colorId] = colorHex;
          }
        }
        colorMap[mergedFacetNode.id] = colorHex;
      }

      j++;

      if (mergedFacetNode.children) {
        for (const child of mergedFacetNode.children) {
          child.parentColorHex = mergedFacetNode.parentColorHex || colorMap[mergedFacetNode.id];
          q.push(child);
        }
      }
    }

    const multipleFacetsColorId = facetDef.id === FacetTypes.mepSystems ? MultipleMepSystemsFacetColorId : MultipleRoomsFacetColorId;
    colorMap[multipleFacetsColorId] = '#5A5A5A';
  }

  return colorMaps;
};

FacetsManager.prototype._applyThemeToModel = function (m, facetIdx, colorMapV4) {

  if (facetIdx === 0) {
    // theme models
    m.setThemingColor(m.getRootId(), colorMapV4[m.urn()], true);
    return;
  }

  // theme facets
  const facetId = this.getFacetDefs()[facetIdx].id;

  const multipleFacetsColorId = facetId === FacetTypes.mepSystems ? MultipleMepSystemsFacetColorId : MultipleRoomsFacetColorId;
  const multipleFacetsColor = colorMapV4[multipleFacetsColorId];

  const filter = this.getFacetDefs()[facetIdx].filter;
  const seenMultiFacetElements = new Map();
  const idsInMultipleFacets = this.idsInMultipleFacets[facetIdx]?.get(m);

  function applyThemeToNodes(mergedFacetNodes) {
    for (let node of mergedFacetNodes) {

      //Only process theming if the facet is not filtered out.
      if (filter.size === 0 || filter.has(node.id)) {
        const facetColor = colorMapV4[node.id];

        const idSet = node.ids.get(m);
        if (idSet) {
          for (let id of idSet) {
            if (idsInMultipleFacets && sortedArrayHas(idsInMultipleFacets, id)) {
              //For elements that belong to potentially multiple facets, color them
              //by the facet color of the first matching facet, and then switch to the multi-filter color
              //in case they are in two or more visible facets
              const status = seenMultiFacetElements.get(id);
              if (!status) {
                seenMultiFacetElements.set(id, 1);
                m.setThemingColor(id, facetColor);
              } else if (status === 1) {
                seenMultiFacetElements.set(id, 2);
                m.setThemingColor(id, multipleFacetsColor);
              }
            } else {
              m.setThemingColor(id, facetColor);
            }
          }
        }
      }

      if (node.children?.length) {
        applyThemeToNodes(node.children);
      }
    }
  }

  applyThemeToNodes(this.mergedFacets[facetIdx]);
};

FacetsManager.prototype.applyTheme = function (facetId, colorMap) {
  const colorMapV4 = this._convertColorMap(colorMap);
  const emptyColorMap = {};

  if (this.currentThemedFacetId && this.currentThemedFacetId !== facetId) {
    // Clear any existing themes
    //TODO: It's not clear why we need to do this explicitly given that
    //the new theme being applied will override the old one for all elements.
    this.clearTheme();
    const facetIdx = this.getFacetDefIndex(this.currentThemedFacetId);
    //facet may no longer be there if the saved view configuration doesn't have it
    if (facetIdx >= 0) {
      this.facetDefs[facetIdx].theme = emptyColorMap;
    }
    this.currentThemedFacetId = undefined;
  }

  //Set new color theme on the requested facet
  const facetIdx = this.getFacetDefIndex(facetId);
  if (facetIdx >= 0) {
    this.currentThemedFacetId = facetId;
    for (let m of this.models) {
      this._applyThemeToModel(m, facetIdx, colorMapV4);
    }
    this.facetDefs[facetIdx].theme = colorMapV4;
    this.facetsEffects.makeRoomsVisible(true);
  } else {
    this.facetsEffects.makeRoomsVisible(false);
  }

  this.viewer.impl.invalidate(true);
};

/**
 * Clears the theme applied to all elements in the facility
 *
 * Note: This does not clear the theme for a specific facet,
 * use applyTheme with an empty colorMap if currentThemedFacetId is defined.
 */
FacetsManager.prototype.clearTheme = function () {
  for (const model of this.models) {
    model.setThemingColor(model.getRootId(), null /* colorMap */, true /* recursive */);
  }

  this.viewer.impl.invalidate(true);
};